<template>
  <div class="mt-5 ehc-interview">
    <div v-if="!loading">
      <v-btn
        class="mt-n1"
        dark
        icon
        large
        absolute
        right
        style="text-align: right"
        color="primary"
        @click="closeInterview"
      >
        <v-icon dark>mdi-close</v-icon>
      </v-btn>

      <ehc-form
        v-if="!isFinishPage"
        v-model="formData"
        :meta="fields[pageNo]"
        @submit="submit()"
        @change="change"
        @valid="isValid = $event"
        @onImageUpload="onImageUpload($event)"
        @select="select($event)"
        :useAutocomplete="useAutocomplete"
        :shakeInvalid="shakeInvalid"
        :user="user"
        :persistent="true"
      >
        <template #qrcode>
          <v-card class="mx-auto" flat>
            <v-card-text>
              <qrcode
                :value="guidebookLink"
                size="200"
                level="H"
                :showTitle="false"
                :showBigButton="false"
                class="mx-auto qrcode"
                style="inline-size: fit-content"
              />
            </v-card-text>
            <p v-html="linkTo(guidebookLink)" class="text-center"></p>
          </v-card>
        </template>
      </ehc-form>

      <div v-else class="ehc-interview__finish pa-2">
        <div class="text-h6 font-weight-bold mb-2">Your guide shell is ready</div>
        <p class="text-body-2 mb-4">
          Starter Property Info and Area Guide pages are in place. Personalize
          that content next so guests get answers that match your property.
        </p>

        <v-list dense class="mb-3">
          <v-list-item class="px-0">
            <v-list-item-content>
              <v-list-item-title>Open your guidebook</v-list-item-title>
              <v-list-item-subtitle>
                Review the home screen and starter pages as a guest would.
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <ehc-btn small color="primary" dark @click="openGuidebook">
                Open
              </ehc-btn>
            </v-list-item-action>
          </v-list-item>

          <v-list-item class="px-0">
            <v-list-item-content>
              <v-list-item-title>Edit Property Info</v-list-item-title>
              <v-list-item-subtitle>
                Sign in to the guidebook, then open Property Info from the menu.
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <ehc-btn small outlined :dark="false" @click="openGuidebookEditor">
                Open
              </ehc-btn>
            </v-list-item-action>
          </v-list-item>

          <v-list-item class="px-0">
            <v-list-item-content>
              <v-list-item-title>Share / print QR</v-list-item-title>
              <v-list-item-subtitle>
                Grab the QR code to place in your listing or on-site.
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <ehc-btn small outlined :dark="false" @click="goToQrStep">
                View QR
              </ehc-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <v-card outlined class="pa-3 mb-4 ehc-interview__training">
          <div class="text-subtitle-1 font-weight-medium mb-1">
            Next: personalize your guide
          </div>
          <p class="text-body-2 mb-3">
            Watch a short training video on editing Property Info, publishing,
            and sharing your guidebook with guests.
          </p>
          <ehc-btn
            color="primary"
            dark
            block
            class="mb-2"
            @click="openTrainingVideo"
          >
            <v-icon left small>mdi-play-circle</v-icon>
            Watch training video
          </ehc-btn>
          <p class="text-caption text--secondary mb-0">
            Prefer the full playlist?
            <a href="#" @click.prevent="goToSupport">
              More training videos
            </a>
          </p>
        </v-card>

        <ehc-btn color="success" dark block @click="finishAndOpenGuidebook">
          Open guidebook and finish
        </ehc-btn>
      </div>

      <ehc-interview-toolbar-bottom
        v-if="!isFinishPage || pageNo > 0"
        :formData="formData"
        :fieldsLength="fieldsLength"
        :pageNo="pageNo"
        :setupTrainingVideoUrl="setupInterviewTrainingVideoUrl"
        @nextInterview="nextInterview(formData.propertyId)"
        @backInterview="backInterview(formData.propertyId)"
        @openSetupTraining="openSetupTrainingVideo"
      />
    </div>
    <div v-else>
      <v-progress-linear
        indeterminate
        color="primary"
        reverse
      ></v-progress-linear>
    </div>
  </div>
</template>

<script>
import apiMixin from "@/mixins/api.vue";
import fieldsForSetup from "@/mixins/fieldsForSetup.vue";
import ehcInterviewToolbarBottom from "./ehc-interview-toolbar-bottom.vue";
import QrCodeVue from "qrcode.vue";
import qrcode from "@/components/ehc-qrcode.vue";
import firebase from "firebase";
import {
  DEFAULT_CUSTOMIZE_GUIDE_VIDEO_URL,
  DEFAULT_SETUP_INTERVIEW_VIDEO_URL,
  DEFAULT_EDIT_SECTION_VIDEO_URL,
  configuredVideoUrl,
} from "@/utils/trainingVideos";

export default {
  components: { ehcInterviewToolbarBottom, QrCodeVue, qrcode },
  mixins: [apiMixin, fieldsForSetup],
  props: {
    propertyId: {
      type: String,
      required: true,
    },
    page: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      property: {},
      pageNo: 0,
      formData: {},
      isValid: false,
      shakeInvalid: false,
      useAutocomplete: false,
      loading: false,
      propertyTypeSelected: null,
      showAddress: null,
      showSplashScreen: null,
      bgImages: [],
      completingSetup: false,
    };
  },
  methods: {
    async getBackgroundImagesArray() {
      const bgImages = await this.apiGetBackgroundImages();
      this.bgImages = bgImages
        .filter((bgImage) => bgImage.active)
        .sort((a, b) => a.text.localeCompare(b.text))
        .map((bgImage) => ({
          value: bgImage.name,
          text: bgImage.text,
        }));
    },

    closeInterview() {
      this.pageNo = 0;
      this.$emit("closeInterview");
    },

    setFormData: function () {
      let obj = { ...this.property };
      try {
        obj = { ...obj, ...this.property.contactInfo };
      } catch (error) {
        console.log("Error: ", error);
      }
      if (
        obj.avatarImg ==
        "https://firebasestorage.googleapis.com/v0/b/everhostio.appspot.com/o/propertyPictures%2Fdefault128211654638639031500x500?alt=media&token=d6982373-43ae-4f36-9ad1-8254450f0f97"
      ) {
        obj.avatarImg =
          "https://firebasestorage.googleapis.com/v0/b/everhostio.appspot.com/o/propertyPictures%2F10122261671219428819500x500?alt=media&token=b9fc322d-7728-413c-bcc5-fb8dd50e0567";
      }
      if (obj.showName === undefined) {
        obj.showName = true;
      }
      if (obj.showAddress === undefined) {
        obj.showAddress = false;
      }
      this.formData = { ...obj };
    },

    updateProperty: async function (propertyId, extra = {}) {
      let contactInfo = this.makeContactObj();
      if (this.formData.bookagain) {
        this.formData.bookagain = this.apiPrependHttp(this.formData.bookagain);
      }
      this.property = { ...this.formData, contactInfo, ...extra };
      await this.apiPutProperty(propertyId, this.property);
    },

    makeContactObj() {
      return {
        hostName: this.formData.contactInfo_hostName || null,
        hostPhotoUrl: this.formData.contactInfo_hostPhotoUrl || null,
        arbnbBool: this.formData.contactInfo_arbnbBool || null,
        vrboBool: this.formData.contactInfo_vrboBool || null,
        homeAwayBool: this.formData.contactInfo_homeAwayBool || null,
        bookingBool: this.formData.contactInfo_bookingBool || null,
        callingNumber: this.formData.contactInfo_callingNumber || null,
        smsNumber: this.formData.contactInfo_smsNumber || null,
      };
    },

    async onImageUpload(payload) {
      this.formData[payload.key] = payload.fileName;
    },

    goToProperty(prop) {
      const url = this.appSite + "login/" + prop.propertyId;
      window.open(url, "", "width=900, height=812");
    },

    openGuidebook() {
      this.goToProperty({ propertyId: this.propertyId });
    },

    openGuidebookEditor() {
      const url = this.appSite + "login/" + this.propertyId;
      window.open(url, "", "width=900, height=812");
    },

    openTrainingVideo() {
      if (!this.editSectionTrainingVideoUrl) {
        return;
      }
      window.open(
        this.editSectionTrainingVideoUrl,
        "_blank",
        "noopener,noreferrer",
      );
    },

    openSetupTrainingVideo() {
      if (!this.setupInterviewTrainingVideoUrl) {
        return;
      }
      window.open(
        this.setupInterviewTrainingVideoUrl,
        "_blank",
        "noopener,noreferrer",
      );
    },

    goToQrStep() {
      this.pageNo = Math.max(this.fieldsLength - 2, 0);
    },

    async markSetupComplete() {
      if (this.completingSetup) {
        return;
      }
      this.completingSetup = true;
      try {
        const completedAt = Date.now();
        await this.updateProperty(this.propertyId, {
          setupCoachCompletedAt: completedAt,
        });
        this.formData.setupCoachCompletedAt = completedAt;
        this.property.setupCoachCompletedAt = completedAt;
      } finally {
        this.completingSetup = false;
      }
    },

    async finishAndOpenGuidebook() {
      await this.markSetupComplete();
      this.closeInterview();
      this.goToProperty({ propertyId: this.propertyId });
    },

    goToSupport() {
      this.closeInterview();
      this.$router.push("/support/how-to-videos");
    },

    async nextInterview(propertyId) {
      if (this.pageNo >= this.fieldsLength - 2) {
        await this.updateProperty(propertyId);
        await this.markSetupComplete();
        this.pageNo = this.fieldsLength - 1;
        return;
      }
      await this.updateProperty(propertyId);
      this.pageNo++;
    },

    backInterview() {
      if (this.pageNo < 1) {
        this.closeInterview();
      } else {
        this.pageNo--;
      }
    },

    async select(type) {
      console.log("selected", type);
      this.propertyTypeSelected = type;
      this.formData.type = type;

      const templateId = this.resolveTemplateIdForType(type);
      const currentSource =
        this.property?.templateSourceId ||
        this.formData?.templateSourceId ||
        "default12821";

      if (!templateId || templateId === currentSource) {
        return;
      }

      try {
        this.loading = true;
        const applyPropertyTemplate = firebase
          .functions()
          .httpsCallable("applyPropertyTemplate");
        const result = await applyPropertyTemplate({
          propertyId: this.propertyId,
          templateId,
          propertyType: type,
        });
        if (result?.data?.property) {
          this.property = result.data.property;
          this.setFormData();
          this.formData.type = type;
          this.propertyTypeSelected = type;
        } else {
          await this.getProperty();
          this.setFormData();
          this.formData.type = type;
          this.propertyTypeSelected = type;
        }
      } catch (error) {
        console.error("Failed to apply property type template", error);
      } finally {
        this.loading = false;
      }
    },

    resolveTemplateIdForType(propertyType) {
      const defaults = {
        default: "default12821",
        hospitalBrazil: "hosp_tmpl_br",
        hospital: "hosp_tmpl",
      };
      const fromSettings =
        this.$store.getters.appSettings?.propertyTypeTemplates || {};
      const map = { ...defaults, ...fromSettings };
      const typed = String(map[propertyType] || "").trim();
      if (typed) {
        return typed;
      }
      return String(map.default || "default12821").trim() || "default12821";
    },

    async getProperty() {
      this.loading = true;
      this.property = await this.apiGetDoc("properties", this.propertyId);
    },

    isSelected(type) {
      if (this.propertyTypeSelected === type) {
        return true;
      } else if (this.formData.type === type) {
        return true;
      } else {
        return false;
      }
    },

    clearFormDataAndProperty: function () {
      this.formData = {};
      this.property = {};
    },

    change: function () {
      this.showAddress = this.formData.showAddress;
      this.showSplashScreen = this.formData.showSplashScreen;
      this.$emit("change", this.formData);
    },
    linkTo: function (url) {
      return `<a href="${url}" target="_blank">${url}</a>`;
    },
  },

  computed: {
    fieldsLength() {
      return this.fields.length;
    },
    isFinishPage() {
      return this.pageNo === this.fieldsLength - 1;
    },
    appSite() {
      return this.$store.getters.appSite;
    },
    isSmallScreen() {
      return window.innerWidth < 900;
    },
    windowWidth() {
      return window.innerWidth;
    },
    trigger: function () {
      return this.$store.getters.trigger;
    },
    guidebookLink: function () {
      return this.appSite + "/" + this.propertyId;
    },
    trainingVideoUrl() {
      return DEFAULT_CUSTOMIZE_GUIDE_VIDEO_URL;
    },
    setupInterviewTrainingVideoUrl() {
      return configuredVideoUrl(
        this.$store.getters.appSettings?.setupInterviewTrainingVideoUrl,
        DEFAULT_SETUP_INTERVIEW_VIDEO_URL,
      );
    },
    newOwnerTrainingVideoUrl() {
      return configuredVideoUrl(
        this.$store.getters.appSettings?.newOwnerTrainingVideoUrl,
        DEFAULT_CUSTOMIZE_GUIDE_VIDEO_URL,
      );
    },
    editSectionTrainingVideoUrl() {
      return (
        configuredVideoUrl(
          this.$store.getters.appSettings?.editSectionTrainingVideoUrl,
          DEFAULT_EDIT_SECTION_VIDEO_URL,
        ) || this.newOwnerTrainingVideoUrl
      );
    },
  },

  watch: {
    propertyId: async function () {
      await this.clearFormDataAndProperty();
      await this.getProperty();
      this.setFormData();
    },
    formData() {
      this.showAddress = this.formData.showAddress;
      this.loading = false;
    },
    "$attrs.trigger": async function () {
      await this.clearFormDataAndProperty();
      await this.getProperty();
      this.setFormData();
    },
  },

  async mounted() {
    this.clearFormDataAndProperty();
    await this.getProperty();
    await this.setFormData();
    this.showAddress = this.formData.showAddress;
    this.showSplashScreen = this.formData.showSplashScreen;
    this.getBackgroundImagesArray();
  },
};
</script>

<style scoped>
</style>
